import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

function loadScript() {
  if (window.turnstile) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SCRIPT;
    s.async = true;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

// Single bot-check hook: no-op (token null) until a Turnstile site key is configured server-side.
export function useBotCheck() {
  const [siteKey, setSiteKey] = useState(null);
  const slotRef = useRef(null);
  const widget = useRef(null);
  const pending = useRef(null);

  useEffect(() => {
    api
      .get("/config")
      .then((r) => setSiteKey(r.data.turnstile_site_key || null))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!siteKey || !slotRef.current) return;
    loadScript()
      .then(() => {
        widget.current = window.turnstile.render(slotRef.current, {
          sitekey: siteKey,
          execution: "execute",
          appearance: "interaction-only",
          callback: (token) => pending.current && pending.current(token),
          "error-callback": () => pending.current && pending.current(null),
        });
      })
      .catch(() => {});
  }, [siteKey]);

  const getToken = useCallback(() => {
    if (!siteKey || !window.turnstile || widget.current == null) return Promise.resolve(null);
    return new Promise((resolve) => {
      pending.current = resolve;
      window.turnstile.reset(widget.current);
      window.turnstile.execute(widget.current);
    });
  }, [siteKey]);

  return { enabled: !!siteKey, slotRef, getToken };
}
